import type { ProjectSummary } from '~/calculations/projectSummary'
import type { CalendarDate, IsoTimestamp } from '~/types/dates'
import { toCalendarDate, unwrapDate } from '~/types/dates'
import { newId, type ProjectId } from '~/types/ids'
import type { Result } from '~/utils/result'
import { err, ok } from '~/utils/result'
import { issue, type RecordEnvelope, type ValidationIssues } from './common'
import type { RouteRecommendation } from './routeRecommendation'
import { DB_SCHEMA_VERSION } from './schemaVersion'

/**
 * A body project (D-062).
 *
 * A project wraps one or more phases and is the unit the user actually thinks
 * in: a named, bounded transformation with a starting condition, the
 * recommendation that justified it, its weekly reviews, and — once the user
 * explicitly ends it — a completion summary. **The phase remains the rules
 * authority.** Nothing about phase behaviour, phase weeks (D-034) or
 * phase-driven targets changes because a project exists above them.
 */

/**
 * What the user says they want (D-062).
 *
 * Stable vocabulary keys with labels resolved at render, mirroring the context
 * tags (D-040): a seventh outcome can be added later without a schema
 * migration. **Setup asks for a destination, never a route** — there is
 * deliberately no "cut / maintain / lean bulk / recomp" among these, because
 * that is the question the user cannot answer and the reason the route engine
 * exists.
 *
 * `prepareForEvent` carries no timing here. How far away the event is changes
 * the route (D-071 routes anything under eight weeks to maintenance at every
 * band), and that comes from the event date collected alongside the outcome
 * rather than from a second key.
 */
export const DESIRED_OUTCOMES = [
  'visibleAbs',
  'loseBellyFat',
  'lookBetterInClothes',
  'buildMuscle',
  'prepareForEvent',
  'maintainPhysique',
] as const

export type DesiredOutcome = (typeof DESIRED_OUTCOMES)[number]

/** Display labels. Presentation only — never persisted, never compared. */
export const DESIRED_OUTCOME_LABELS: Readonly<Record<DesiredOutcome, string>> = {
  visibleAbs: 'Visible abs',
  loseBellyFat: 'Lose belly fat',
  lookBetterInClothes: 'Look better in clothes',
  buildMuscle: 'Build muscle',
  prepareForEvent: 'Prepare for an event',
  maintainPhysique: 'Maintain current physique',
}

export function isDesiredOutcome(value: string): value is DesiredOutcome {
  return (DESIRED_OUTCOMES as readonly string[]).includes(value)
}

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const

/**
 * A starting name — "Project October 2026", the D-062 example shape.
 *
 * Every project needs a name from the moment it exists, including the one the
 * schema v2 migration creates for history that predates the wizard. This is a
 * suggestion the user edits, not a label the app insists on.
 */
export function defaultProjectName(targetDate: CalendarDate): string {
  const [year, month] = unwrapDate(targetDate).split('-')
  const name = MONTH_NAMES[Number(month) - 1]
  return name === undefined ? `Project ${year}` : `Project ${name} ${year}`
}

/**
 * Has the project begun? (D-107 §2.)
 *
 * A project may be created days before it starts, so existing and running are
 * different questions. **Waiting is derived here and is never a `ProjectStatus`
 * member**: `getActiveProject` queries the status index for `'active'`, so a
 * `'waiting'` row would be invisible to every reader, `getOnboardingState` would
 * report `firstRun` to a user whose height and photos the app is holding, and
 * `completeProjectSetup`'s guard would stop refusing — restoring D-103 §7's
 * two-active-projects fork. `EndProjectRequest.status` is
 * `Exclude<ProjectStatus, 'active'>`, so a new member would also become a silent
 * way to end a project.
 *
 * Pure and clock-free. Services read the waiting state through
 * `getProjectStartState`, which is the one impure accessor; **no caller may
 * re-derive `startDate > today` locally**, because two definitions of "started"
 * is the three-representations problem D-099 §D rejected.
 */
export function projectHasStarted(project: ProjectRecord, today: CalendarDate): boolean {
  // Lexical comparison is exact for zero-padded YYYY-MM-DD.
  return unwrapDate(today) >= unwrapDate(project.startDate)
}

/**
 * How a project ended.
 *
 * `extend` is deliberately absent: extending moves the target date and leaves
 * the project running, so it is an edit rather than an ending. A project ends
 * only by explicit decision and never starts the next one (D-020).
 */
export type ProjectStatus = 'active' | 'completed' | 'abandoned'

export interface ProjectRecord extends RecordEnvelope {
  readonly id: ProjectId
  /** The user's own name for it — "Project December 2026", "Vacation Cut". */
  readonly name: string
  readonly status: ProjectStatus
  readonly startDate: CalendarDate
  readonly targetDate: CalendarDate
  /**
   * The day the project was set up, which is not always the day it starts (D-107 §3).
   *
   * A user may create a project on a Thursday and start it on the Sunday. The
   * measurement session, the weigh-in, the photos and the nutrition plan are all
   * stamped with the day they actually happened — a measurement is evidence of a
   * body on a day, and moving it records something that did not happen. Without
   * this field the project's own starting condition would fall outside its own
   * summary, because `buildProjectSummary` collects evidence from `startDate`.
   *
   * **Optional, and absent on every record written before this existed** — which
   * is what makes it need no migration and no schema version bump, exactly as
   * `destinationChanges` below and `DomainGoalChange.source` at C7.0 phase 5.
   * Read it through `evidenceWindowStart` in `services/projectService.ts`, never
   * directly, and never as the project's duration: "Ran for N weeks" is computed
   * from `startDate` and feeding it this would add a week the project did not run.
   */
  readonly createdOn?: CalendarDate
  /**
   * The destination the user described.
   *
   * Optional because a project **backfilled by the schema v2 migration has
   * none**: it predates the wizard, so the user was never asked. Inventing one
   * would put words in their mouth about why they were already dieting.
   */
  readonly desiredOutcome?: DesiredOutcome
  /** The event this project is aimed at, when the outcome is `prepareForEvent`. */
  readonly eventDate?: CalendarDate
  /** When the project actually ended. Absent while it is running. */
  readonly endedOn?: CalendarDate
  /** Why it ended, recorded at the explicit end decision (D-020). */
  readonly endReason?: string
  readonly notes?: string
  /**
   * The recommendation that justified this project, copied at the moment it was
   * confirmed (D-062).
   *
   * An **immutable value copy**, following the `phaseSnapshot` precedent
   * (D-046): a later edit to the phase or the goal must never retroactively
   * change what Project Sola originally advised. Absent on a project the schema
   * v2 migration created, which predates the route engine.
   */
  readonly recommendationSnapshot?: RouteRecommendation
  /**
   * What the finished project is recorded as having covered (D-062).
   *
   * Written once, at the explicit end, and never recomputed — recomputing it
   * later would run today's counting over data the user has since edited, and
   * report something they never read.
   */
  readonly completionSummary?: ProjectSummary
  /**
   * Every time the destination changed, appended and never overwritten (D-103 §6).
   *
   * The phase-end flow pre-fills the stored `desiredOutcome` and lets the user
   * change it — six months changes what someone wants, and forcing a new project
   * for a changed destination would end a project by side effect, which is the
   * thing §3 exists to stop.
   *
   * **Appended rather than replaced**, following `PhaseCorrection` (D-070) and
   * `DomainGoalChange` (D-099): a destination silently overwritten makes the
   * project's own history a curated argument. `desiredOutcome` above is always
   * the current one, so nothing has to walk this list to read it.
   *
   * **Optional, and absent on every record written before this existed** — which
   * is what makes it need no migration and no schema version bump, exactly as
   * `DomainGoalChange.source` did at C7.0 phase 5.
   *
   * `recommendationSnapshot` is deliberately NOT touched by any of this. It is an
   * immutable value copy of what Project Sola advised at setup (D-046), and
   * writing the phase-end recommendation into it would retroactively change the
   * advice the user originally read and agreed to. The new **phase record** is
   * the durable trace of a phase-end recommendation.
   */
  readonly destinationChanges?: readonly DestinationChange[]
}

/** One recorded change of destination (D-103 §6). */
export interface DestinationChange {
  readonly changedAt: IsoTimestamp
  /** Absent on a project backfilled by the schema v2 migration, which had none. */
  readonly from?: DesiredOutcome
  readonly to: DesiredOutcome
}

export interface ProjectInput {
  readonly name: string
  readonly startDate: string
  readonly targetDate: string
  /** The day setup happened, when it is not the day the project starts (D-107 §3). */
  readonly createdOn?: string
  readonly desiredOutcome?: string
  readonly eventDate?: string
  readonly notes?: string
}

const MAX_NAME_LENGTH = 80

export function validateProject(input: ProjectInput): ValidationIssues {
  const issues: ValidationIssue[] = []

  const name = input.name.trim()
  if (name.length === 0) {
    issues.push(issue('name', 'Give the project a name.'))
  } else if (name.length > MAX_NAME_LENGTH) {
    issues.push(issue('name', `A project name may be at most ${MAX_NAME_LENGTH} characters.`))
  }

  const start = toCalendarDate(input.startDate)
  const target = toCalendarDate(input.targetDate)

  if (start === undefined) {
    issues.push(issue('startDate', 'Start date must be a real date in YYYY-MM-DD form.'))
  }
  if (target === undefined) {
    issues.push(issue('targetDate', 'Target date must be a real date in YYYY-MM-DD form.'))
  }
  // Lexical comparison is exact for zero-padded YYYY-MM-DD.
  if (start !== undefined && target !== undefined && unwrapDate(target) < unwrapDate(start)) {
    issues.push(issue('targetDate', 'Target date must not be before the start date.'))
  }

  issues.push(...createdOnIssues(input.createdOn, start))

  if (input.desiredOutcome !== undefined && !isDesiredOutcome(input.desiredOutcome)) {
    issues.push(
      issue('desiredOutcome', `Desired outcome must be one of: ${DESIRED_OUTCOMES.join(', ')}.`),
    )
  }

  if (input.eventDate !== undefined && toCalendarDate(input.eventDate) === undefined) {
    issues.push(issue('eventDate', 'Event date must be a real date in YYYY-MM-DD form.'))
  }

  return issues
}

type ValidationIssue = ValidationIssues[number]

/**
 * `createdOn`, checked apart from the rest (D-107 §3).
 *
 * Extracted because folding it inline pushed `validateProject` past the
 * cognitive-complexity ceiling, and the working rules say to refactor rather
 * than suppress.
 */
function createdOnIssues(
  createdOn: string | undefined,
  start: CalendarDate | undefined,
): ValidationIssue[] {
  if (createdOn === undefined) return []

  const created = toCalendarDate(createdOn)
  if (created === undefined) {
    return [issue('createdOn', 'Created-on date must be a real date in YYYY-MM-DD form.')]
  }
  /* A project cannot have been set up after it began. The reverse — set up days
     before it starts — is the whole point of the field. */
  if (start !== undefined && unwrapDate(created) > unwrapDate(start)) {
    return [issue('createdOn', 'A project cannot be created after it starts.')]
  }
  return []
}

/**
 * Identity carried across an edit.
 *
 * The same reasoning as `ExistingPhaseIdentity`: an edit must not mint a second
 * project or silently reopen one the user has ended, so the caller supplies the
 * stored identity and the builder preserves it verbatim.
 */
export interface ExistingProjectIdentity {
  readonly id: ProjectId
  readonly status: ProjectStatus
  readonly createdAt: IsoTimestamp
  readonly endedOn?: CalendarDate
  readonly endReason?: string
}

/** Build a validated project record without touching storage. */
export function buildProjectRecord(
  input: ProjectInput,
  now: IsoTimestamp,
  existing?: ExistingProjectIdentity,
): Result<ProjectRecord, ValidationIssues> {
  const issues = validateProject(input)
  if (issues.length > 0) return err(issues)

  const startDate = toCalendarDate(input.startDate)
  const targetDate = toCalendarDate(input.targetDate)
  if (startDate === undefined || targetDate === undefined) {
    return err([issue('project', 'Project dates failed construction.')])
  }

  const eventDate = input.eventDate === undefined ? undefined : toCalendarDate(input.eventDate)
  const createdOn = input.createdOn === undefined ? undefined : toCalendarDate(input.createdOn)

  return ok({
    id: existing?.id ?? newId<'ProjectId'>(),
    name: input.name.trim(),
    status: existing?.status ?? 'active',
    startDate,
    targetDate,
    ...(createdOn === undefined ? {} : { createdOn }),
    ...(input.desiredOutcome === undefined
      ? {}
      : { desiredOutcome: input.desiredOutcome as DesiredOutcome }),
    ...(eventDate === undefined ? {} : { eventDate }),
    /* Carried, not rebuilt: how and when a project ended is history, and an
       ordinary edit must not erase it. */
    ...(existing?.endedOn === undefined ? {} : { endedOn: existing.endedOn }),
    ...(existing?.endReason === undefined ? {} : { endReason: existing.endReason }),
    ...(input.notes === undefined ? {} : { notes: input.notes }),
    createdAt: existing?.createdAt ?? now,
    updatedAt: now,
    schemaVersion: DB_SCHEMA_VERSION,
  })
}
